import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate } from 'react-router-dom';
import { FormOutlined, DownOutlined } from "@ant-design/icons";
import './Model.css'
import axios from "axios";
import { message } from "antd";

const Token = localStorage.getItem("JwtToken");
const USERID = localStorage.getItem('UserId');
const API_URL = process.env.REACT_APP_API_URL;

const ModelSlider = () => {
    let settings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        autoplay: true,
    };

    const [movies, setMovies] = useState([]);
    const [token, setToken] = useState(localStorage.getItem("JwtToken"));
    const [modelExists, setModelExists] = useState(null);
    const [agentExists, setAgentExists] = useState(false);
    const [dancerExists, setDancerExists] = useState(false);
    const [singerExists, setSingerExists] = useState(false);
    const [musicianExists, setMusicianExists] = useState(false);
    const [anchorExists, setAnchorExists] = useState(false);
    const [showAgentDropdown, setShowAgentDropdown] = useState(false);
    const navigate = useNavigate();

    const option1 = {
        headers: {
            'Authorization': `Bearer ${Token}`
        },
    };

    const getSlider = async() => {
        try {
            const res = await axios.get(`${API_URL}/api/slider-for-movie-trailers?populate[0]=movieTrailer.MovieThumbnail&populate[1]=movieTrailer.VideoFile`, option1);
            setMovies(res?.data?.data);
        } catch(err) {
            console.error(err);
        }
    }

    const checkExistingApplications = async() => {
        try {
            const res = await axios.get(`${API_URL}/api/users/${USERID}?populate=model&populate=role&populate=agent_models&populate=dancer&populate=singer&populate=musician&populate=anchor`);
            setModelExists(res?.data?.model);
            setAgentExists(res?.data?.agent_models?.length > 0);
            setDancerExists(res?.data?.dancer !== null);
            setSingerExists(res?.data?.singer !== null);
            setMusicianExists(res?.data?.musician !== null);
            setAnchorExists(res?.data?.anchor !== null);
        } catch (error) {
            console.error("Error checking applications:", error);
        }
    }

    useEffect(() => {
        getSlider();
        if(localStorage.getItem('redirect')) {
            localStorage.removeItem('redirect');
        }
        if(USERID) checkExistingApplications();
    }, [token]);

    const applyNow = async (type, isAgent = false) => {
        if (!token) {
            localStorage.setItem("redirect", window.location.pathname);
            navigate("/login");
            return;
        }

        // For agent-specific forms - allow agents to register multiple talents
        if (isAgent) {
            // Agents can register multiple talents, so no restrictions needed here
            switch(type) {
                case 'Model':
                    navigate("/agentModelForm");
                    break;
                case 'Dancer':
                    navigate("/agentDancerForm");
                    break;
                case 'Singer':
                    navigate("/agentSingerForm");
                    break;
                case 'Musician':
                    navigate("/agentMusicianForm");
                    break;
                case 'Anchor':
                    navigate("/agentAnchorForm");
                    break;
                default:
                    navigate("/agentModelForm");
            }
            return;
        }

        // RESTRICTION 1: If user is already an Agent, block ALL regular applications
        if (agentExists) {
            message.error('As an Agent, you cannot apply to regular talent categories. Please use Agent dropdown to register talents.');
            return;
        }

        // RESTRICTION 2: Check if user already has ANY application (only one category allowed per user)
        const hasAnyApplication = modelExists || dancerExists || singerExists || musicianExists || anchorExists;
        
        if (hasAnyApplication && type !== 'Agent') {
            message.error('You can only register for one category');
            return;
        }

        // RESTRICTION 3: Check if user already has this specific type of application
        switch (type) {
            case 'Agent':
                if (agentExists) {
                    message.error('You already applied as an Agent');
                    return;
                }
                navigate("/agentModelForm");
                break;
            case 'Dancer':
                if (dancerExists) {
                    message.error('You already applied as a Dancer');
                    return;
                }
                navigate("/dancerForm");
                break;
            case 'Singer':
                if (singerExists) {
                    message.error('You already applied as a Singer');
                    return;
                }
                navigate("/singerForm");
                break;
            case 'Musician':
                if (musicianExists) {
                    message.error('You already applied as a Musician');
                    return;
                }
                navigate("/musicianForm");
                break;
            case 'Anchor':
                if (anchorExists) {
                    message.error('You already applied as an Anchor');
                    return;
                }
                navigate("/anchorForm");
                break;
            case 'Model':
            default:
                if (modelExists) {
                    message.error('You already applied as a Model');
                    return;
                }
                navigate("/modelForm");
                break;
        }
    };

    const handleAgentButtonClick = () => {
        // If user is not logged in, redirect to login
        if (!token) {
            localStorage.setItem("redirect", window.location.pathname);
            navigate("/login");
            return;
        }

        // If user is already an agent, just show the dropdown
        if (agentExists) {
            setShowAgentDropdown(!showAgentDropdown);
            return;
        }

        // If user doesn't have any applications yet, navigate to agent form directly
        const hasAnyApplication = modelExists || dancerExists || singerExists || musicianExists || anchorExists;
        if (!hasAnyApplication) {
            navigate("/agentModelForm");
        } else {
            // If user has other applications, show dropdown to choose agent type
            setShowAgentDropdown(!showAgentDropdown);
        }
    };

    const handleDropdownItemClick = (type) => {
        setShowAgentDropdown(false);
        applyNow(type, true);
    };

    return (
        <Carousel {...settings}>
            <Wrap>
                <Info>
                    <Subtitle>Choose your category</Subtitle>
                    <ButtonContainer>
                        <ButtonRow>
                            <Button1 onClick={() => applyNow('Model')}>
                                <FormOutlined /> <ButtonText>Apply As Model</ButtonText>
                            </Button1>
                            <Button4 onClick={() => applyNow('Singer')}>
                                <FormOutlined /> <ButtonText>Apply As Singer</ButtonText>
                            </Button4>
                            <Button3 onClick={() => applyNow('Dancer')}>
                                <FormOutlined /> <ButtonText>Apply As Dancer</ButtonText>
                            </Button3>
                        </ButtonRow>
                        <ButtonRow>
                            <Button5 onClick={() => applyNow('Musician')}>
                                <FormOutlined /> <ButtonText>Apply As Musician</ButtonText>
                            </Button5>
                            <Button6 onClick={() => applyNow('Anchor')}>
                                <FormOutlined /> <ButtonText>Apply As Anchor</ButtonText>
                            </Button6>
                            <AgentButtonWrapper 
                                onMouseEnter={() => setShowAgentDropdown(true)}
                                onMouseLeave={() => setShowAgentDropdown(false)}
                            >
                                <Button2 onClick={handleAgentButtonClick}>
                                    <FormOutlined /> <ButtonText>Apply As Agent</ButtonText> <DownOutlined />
                                </Button2>
                                {showAgentDropdown && (
                                    <AgentDropdown>
                                        <DropdownItem onClick={() => handleDropdownItemClick('Model')}>Agent Model</DropdownItem>
                                        <DropdownItem onClick={() => handleDropdownItemClick('Singer')}>Agent Singer</DropdownItem>
                                        <DropdownItem onClick={() => handleDropdownItemClick('Dancer')}>Agent Dancer</DropdownItem>
                                        <DropdownItem onClick={() => handleDropdownItemClick('Musician')}>Agent Musician</DropdownItem>
                                        <DropdownItem onClick={() => handleDropdownItemClick('Anchor')}>Agent Anchor</DropdownItem>
                                    </AgentDropdown>
                                )}
                            </AgentButtonWrapper>
                        </ButtonRow>
                    </ButtonContainer>
                </Info>  
                <Overlays>
                    <img src={`https://api.moviemads.com/uploads/Model_Thumbnail_17d55ca8c9.jpg`} alt="Img"/>
                </Overlays>
            </Wrap>
        </Carousel>
    );
}

export default ModelSlider;

const AgentButtonWrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    
    @media (max-width: 768px) {
        width: 100%;
    }
`;

const AgentDropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    background-color: #f9f9f9;
    min-width: 160px;
    box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
    z-index: 10;
    border-radius: 5px;
    overflow: hidden;
    
    @media (max-width: 768px) {
        min-width: 140px;
        left: 50%;
        transform: translateX(-50%);
    }
`;

const DropdownItem = styled.div`
    color: black;
    padding: 12px 16px;
    text-decoration: none;
    display: block;
    cursor: pointer;
    background-color: #fba010;
    color: #000;
    font-weight: bold;
    border: 2px solid #000;
    
    &:hover {
        background-color: #303030;
        color: #fff;
    }
    
    @media (max-width: 768px) {
        padding: 10px 12px;
        font-size: 12px;
        text-align: center;
        border: 1.5px solid #000;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-top: 20px;
    width: 100%;
    max-width: 800px;
    margin-left: 190px;
    
    @media (max-width: 768px) {
        margin-left: 0;
        width: 100%;
        justify-content: center;
    }
`;

const ButtonRow = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    width: 100%;
    
    @media (max-width: 768px) {
        gap: 8px;
        flex-wrap: wrap;
    }
`;

const ButtonText = styled.span`
    @media (max-width: 480px) {
        font-size: 10px;
    }
`;

// Base button style for consistency
const BaseButton = styled.button`
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-width: 160px;
    border: 2px solid #000;
    transition: all 0.3s ease;
    text-align: center;
    
    &:hover {
        background-color: #303030;
        color: #fff;
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.2);
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
        
        &:hover {
            background-color: inherit;
            color: inherit;
            transform: none;
            box-shadow: none;
        }
    }
    
    @media (max-width: 768px) {
        font-size: 12px;
        padding: 10px 8px;
        min-width: 100px;
        gap: 4px;
        border-radius: 6px;
        flex: 1;
        max-width: 110px;
        border: 1.5px solid #000;
        height: 40px;
    }
    
    @media (max-width: 480px) {
        font-size: 10px;
        padding: 8px 6px;
        min-width: 90px;
        max-width: 100px;
        height: 38px;
    }
    
    @media (max-width: 360px) {
        min-width: 85px;
        max-width: 90px;
        font-size: 9px;
        padding: 6px 4px;
    }
`;

// Individual button styles
const Button1 = styled(BaseButton)`
    background-color: #ff0015;
    color: #ffffff;
`;

const Button2 = styled(BaseButton)`
    background: #fba010;
    color: #000;
`;

const Button3 = styled(BaseButton)`
    background: #4CAF50;
    color: #fff;
`;

const Button4 = styled(BaseButton)`
    background: #2196F3;
    color: #fff;
`;

const Button5 = styled(BaseButton)`
    background: rgb(131, 33, 243);
    color: #fff;
`;

const Button6 = styled(BaseButton)`
    background: #f027b3ff;
    color: #fff;
`;

const Carousel = styled(Slider)`
    ul li button:before {
        font-size: 10px;
        color: rgb(150, 158, 171);
    }
    
    .slick-next:before {
        color: #ff0015;
        font-size: 40px;
        font-weight: bold;
        opacity: 100%;
    }
    
    .slick-prev:before {
        color: #ff0015;
        font-size: 40px;
        font-weight: bold;
        opacity: 100%;
    }
    
    @media (max-width: 768px) {
        .slick-next:before {
            display: none;
        }
        .slick-prev:before {
            display: none;
        }
    }

    li.slick-active button::before {
        color: white;
    }
    
    .slick-list {
        overflow: visible;
    }

    button {
        z-index: 1;
    }
`;

const Wrap = styled.div`
    position: relative;
    
    &:after {
        content: "";
        position: absolute;
        width: 100%;
        height: 45%;
        background: linear-gradient(to bottom, rgba(255, 255, 255, 0)0%, #000000 100%);
        bottom: 0;
        right: 0;
        pointer-events: none;
        
        @media (max-width: 768px) {
            height: 70%;
        }
    }

    cursor: pointer;
    object-fit: cover;
    object-position: center center;
    height: 45vh;
    
    @media (max-width: 768px) {
        height: 35vh;
    }
    
    @media (max-width: 480px) {
        height: 40vh;
    }
    
    img {
        border: 5px solid transparent;
        opacity: 0.8;
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 10px;
        box-shadow: rgb(0 0 0 / 69%) 0px 26px 30px -10px,
                   rgb(0 0 0 / 73%) 0px 16px 10px -10px;
        transition-duration: 300ms;
    }
`;

const Info = styled.div`
    position: absolute;
    z-index: 3;
    top: 50%;
    left: 50%;
    justify-content: center;
    align-items: center;
    text-align: center;
    transform: translate(-50%, -50%);
    width: 100%;
    padding: 0 20px;
    
    @media (max-width: 768px) {
        padding: 0 15px;
        top: 45%;
    }
    
    @media (max-width: 480px) {
        padding: 0 10px;
        top: 40%;
    }
`;

const Subtitle = styled.h2`
    color: #fff;
    font-size: 32px;
    margin-bottom: 20px;
    text-transform: uppercase;
    opacity: 0.8;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
    
    @media (max-width: 768px) {
        font-size: 20px;
        margin-bottom: 15px;
        line-height: 1.3;
    }
    
    @media (max-width: 480px) {
        font-size: 18px;
        margin-bottom: 12px;
    }
`;

const Overlays = styled.div`
    background: rgba(0, 0, 0, 0.9);
    width: 100%;
    height: 100%;
`;